// src/oura/components/newscan/openDecisions.ts
//
// Which decisions are open, worst first. One query for the Review step's
// head and foot (how many are left) and the decision list (what it shows).

import { isFirstCheckDraft, type DecisionSeverity, type ScanDraft, type ScanDraftDecision } from "@service/aeo/scanDraftTypes";

const RANK: Record<DecisionSeverity, number> = { blocker: 0, decide: 1, suggestion: 2, info: 3 };

/**
 * Decisions the Review never lists: the pitch line (`presale_set`, said under
 * the step head instead) and, on a First AI check, a trend break (the client
 * has no trend yet, JOURNEY-SPEC §2.5).
 */
function shown(draft: ScanDraft, d: ScanDraftDecision): boolean {
    if (d.type === "presale_set") return false;
    return !(d.type === "trend_break" && isFirstCheckDraft(draft));
}

/** Open decisions of the given severities, worst first, in the server's order within a severity. */
export function openDecisions(draft: ScanDraft, severities: DecisionSeverity[]): ScanDraftDecision[] {
    return draft.decisions
        .map((d, i) => ({ d, i }))
        .filter(({ d }) => d.state === "open" && severities.includes(d.severity) && shown(draft, d))
        .sort((a, b) => RANK[a.d.severity] - RANK[b.d.severity] || a.i - b.i)
        .map(({ d }) => d);
}

/** An open decision that gates approval. */
export type GateDecision = ScanDraftDecision & { severity: "blocker" | "decide" };

/** The open blocker and decide decisions, worst first: what stands between the draft and Approve. */
export function openGates(draft: ScanDraft): GateDecision[] {
    return openDecisions(draft, ["blocker", "decide"]) as GateDecision[];
}
